import React, { useState } from 'react';
import { saveCampaign } from '../services/campaignService';

const GoalIcons = {
  brand_awareness: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  lead_gen:        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  sales:           <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  engagement:      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
};

const GOALS = [
  { value: 'brand_awareness', label: 'Markenbekanntheit', desc: 'Reichweite und Sichtbarkeit erhöhen' },
  { value: 'lead_gen',        label: 'Lead-Generierung',  desc: 'Neue Kontakte und Anfragen gewinnen' },
  { value: 'sales',           label: 'Verkäufe steigern', desc: 'Direkten Umsatz generieren' },
  { value: 'engagement',      label: 'Engagement',        desc: 'Community aufbauen und aktivieren' },
];

const STEPS = ['Basis', 'Budget & Laufzeit', 'Zielgruppe', 'Ziel', 'Plan generieren'];

function StepIndicator({ current }) {
  return (
    <div className="mb-8">
      {/* Circles + lines row */}
      <div className="flex items-center justify-center">
        {STEPS.map((_, i) => (
          <React.Fragment key={i}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all shrink-0 ${
              i < current   ? 'bg-indigo-600 text-white' :
              i === current ? 'bg-indigo-600 text-white ring-4 ring-indigo-100' :
              'bg-gray-100 text-gray-400'
            }`}>{i < current ? '✓' : i + 1}</div>
            {i < STEPS.length - 1 && (
              <div className={`h-0.5 w-8 transition-all ${i < current ? 'bg-indigo-600' : 'bg-gray-100'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
      {/* Labels row */}
      <div className="hidden sm:flex items-start justify-center mt-2">
        {STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <div className="w-7 flex justify-center">
              <span className={`text-xs text-center leading-tight ${i === current ? 'text-indigo-600 font-medium' : 'text-gray-400'}`} style={{width: 56, marginLeft: -14}}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && <div className="w-8 shrink-0" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function PlanSection({ title, icon, children, rationale }) {
  const [showRationale, setShowRationale] = useState(false);
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <span>{icon}</span> {title}
        </h3>
        {rationale && (
          <button
            onClick={() => setShowRationale(v => !v)}
            className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-1 transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3"/><path d="M7 6v4M7 4.5h.01" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
            {showRationale ? 'Begründung ausblenden' : 'Strategische Begründung'}
          </button>
        )}
      </div>
      {showRationale && rationale && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-xs text-indigo-700 leading-relaxed">
          <strong>Strategische Begründung:</strong> {rationale}
        </div>
      )}
      {children}
    </div>
  );
}

function PlanResult({ plan, campaign, onSave, onReset }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(plan);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (social) => {
    setPublishTarget(social.platform);
    setPublishing(true);
    setPublishResult(null);
    try {
      const res = await fetch('/.netlify/functions/social-publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform: social.platform.toLowerCase().replace(' (facebook)', '').trim(),
          content: social.caption,
          caption: social.caption,
          accessToken: 'OAUTH_TOKEN_PLACEHOLDER',
        }),
      });
      const data = await res.json();
      setPublishResult(data.success ? { ok: true, url: data.url } : { ok: false, error: data.error });
    } catch (e) {
      setPublishResult({ ok: false, error: e.message });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Dein Kampagnenplan</h2>
          <p className="text-xs text-gray-400 mt-0.5">KI-generiert · {new Date().toLocaleDateString('de-DE')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onReset} className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5 border border-gray-200 rounded-lg transition-colors">
            Neu planen
          </button>
          <button
            onClick={handleSave}
            disabled={saving || saved}
            className="text-xs text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 px-4 py-1.5 rounded-lg font-medium transition-all"
          >
            {saving ? 'Speichern…' : saved ? '✓ Gespeichert' : 'Plan speichern'}
          </button>
        </div>
      </div>

      {/* Summary */}
      {plan.summary && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-5 mb-4">
          <p className="text-sm text-indigo-800 font-medium leading-relaxed">{plan.summary}</p>
        </div>
      )}

      {/* Poster */}
      {plan.poster && (
        <PlanSection title="Plakat-Konzept" icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>} rationale={plan.poster.rationale}>
          <div className="space-y-2 text-sm">
            <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Headline</span><p className="text-gray-900 font-medium mt-0.5">{plan.poster.headline}</p></div>
            <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Subline</span><p className="text-gray-700 mt-0.5">{plan.poster.subline}</p></div>
            <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Visuelles Konzept</span><p className="text-gray-600 mt-0.5 text-xs leading-relaxed">{plan.poster.visualConcept}</p></div>
            <div className="pt-2"><span className="inline-flex items-center gap-1 bg-indigo-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">{plan.poster.callToAction}</span></div>
          </div>
        </PlanSection>
      )}

      {/* Flyer */}
      {plan.flyer && (
        <PlanSection title="Flyer-Inhalt" icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>} rationale={plan.flyer.rationale}>
          <div className="space-y-3 text-sm">
            <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Headline</span><p className="text-gray-900 font-medium mt-0.5">{plan.flyer.headline}</p></div>
            <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fließtext</span><p className="text-gray-600 mt-0.5 text-xs leading-relaxed">{plan.flyer.bodyText}</p></div>
            {plan.flyer.bulletPoints?.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Vorteile</span>
                <ul className="mt-1 space-y-1">
                  {plan.flyer.bulletPoints.map((bp, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                      <svg width="10" height="10" viewBox="0 0 14 14" fill="none" className="mt-0.5 shrink-0"><path d="M2 7L5.5 10.5L12 3.5" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      {bp}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="pt-1"><span className="inline-flex items-center gap-1 bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">{plan.flyer.callToAction}</span></div>
          </div>
        </PlanSection>
      )}

      {/* Video Script */}
      {plan.videoScript && (
        <PlanSection title="Video-Skript" icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 10l4.553-2.277A1 1 0 0121 8.65v6.7a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>} rationale={plan.videoScript.rationale}>
          <div className="space-y-3 text-sm">
            <div className="flex gap-4">
              <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Länge</span><p className="text-gray-900 font-medium mt-0.5">{plan.videoScript.duration} Sek.</p></div>
            </div>
            <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Hook (erste 3 Sek.)</span><p className="text-gray-800 mt-0.5 italic text-xs">"{plan.videoScript.hook}"</p></div>
            <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kernbotschaft</span><p className="text-gray-700 mt-0.5 text-xs">{plan.videoScript.mainMessage}</p></div>
            {plan.videoScript.scenes?.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Szenen</span>
                <div className="mt-2 space-y-2">
                  {plan.videoScript.scenes.map(s => (
                    <div key={s.scene} className="flex gap-3 bg-gray-50 rounded-xl p-3">
                      <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-600 shrink-0">{s.scene}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-700 leading-relaxed">{s.description}</p>
                        <span className="text-xs text-gray-400 mt-0.5 block">{s.duration}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Abschluss-CTA</span><p className="text-gray-800 mt-0.5 text-xs">"{plan.videoScript.closingCTA}"</p></div>
          </div>
        </PlanSection>
      )}

      {/* Social Media */}
      {plan.socialMedia?.length > 0 && (
        <PlanSection title="Social Media Posts" icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 18.5a6.5 6.5 0 100-13 6.5 6.5 0 000 13z" stroke="currentColor" strokeWidth="1.5"/><path d="M12 5.5V3M12 21v-2.5M3 12H5.5M18.5 12H21M5.636 5.636l1.768 1.768M16.596 16.596l1.768 1.768M5.636 18.364l1.768-1.768M16.596 7.404l1.768-1.768" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>} rationale={null}>
          <div className="space-y-4">
            {plan.socialMedia.map((s, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{s.platform}</span>
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{s.contentType}</span>
                  </div>
                  <button
                    onClick={() => handlePublish(s)}
                    disabled={publishing && publishTarget === s.platform}
                    className="text-xs text-indigo-600 border border-indigo-200 hover:bg-indigo-50 disabled:opacity-50 px-3 py-1 rounded-lg transition-all flex items-center gap-1"
                  >
                    {publishing && publishTarget === s.platform
                      ? <><svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Sende…</>
                      : 'Veröffentlichen'
                    }
                  </button>
                </div>
                {publishResult && publishTarget === s.platform && (
                  <div className={`mb-3 p-2 rounded-lg text-xs ${publishResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                    {publishResult.ok ? `✓ Veröffentlicht: ${publishResult.url}` : `Fehler: ${publishResult.error}`}
                  </div>
                )}
                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line mb-3">{s.caption}</p>
                <div className="flex gap-4 text-xs text-gray-400">
                  <span>🕐 {s.postingTime}</span>
                  <span>📅 {s.frequency}</span>
                </div>
                {s.rationale && (
                  <details className="mt-3">
                    <summary className="text-xs text-indigo-500 cursor-pointer hover:text-indigo-700">Strategische Begründung</summary>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{s.rationale}</p>
                  </details>
                )}
              </div>
            ))}
          </div>
        </PlanSection>
      )}

      {/* Budget Allocation */}
      {plan.budgetAllocation && (
        <PlanSection title="Budget-Verteilung" icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>} rationale={null}>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(plan.budgetAllocation).map(([key, val]) => {
              const labels = { print: 'Print', socialAds: 'Social Ads', production: 'Produktion', reserve: 'Reserve' };
              const colors = { print: 'bg-indigo-100 text-indigo-700', socialAds: 'bg-purple-100 text-purple-700', production: 'bg-emerald-100 text-emerald-700', reserve: 'bg-gray-100 text-gray-600' };
              return (
                <div key={key} className={`rounded-xl p-3 ${colors[key] || 'bg-gray-100 text-gray-600'}`}>
                  <p className="text-xs font-semibold">{labels[key] || key}</p>
                  <p className="text-lg font-bold mt-0.5">{val.amount} €</p>
                  <p className="text-xs opacity-70">{val.percentage}% des Budgets</p>
                </div>
              );
            })}
          </div>
        </PlanSection>
      )}

      {/* Timeline */}
      {plan.timeline?.length > 0 && (
        <PlanSection title="Zeitplan" icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>} rationale={null}>
          <div className="space-y-3">
            {plan.timeline.map((t, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-16 shrink-0">
                  <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">Woche {t.week}</span>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-800 mb-1">{t.phase}</p>
                  <ul className="space-y-0.5">
                    {t.activities?.map((a, j) => (
                      <li key={j} className="text-xs text-gray-500 flex items-start gap-1.5">
                        <span className="text-gray-300 mt-0.5">·</span> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </PlanSection>
      )}
    </div>
  );
}

export default function CampaignPlanner({ user, onClose }) {
  const [step, setStep]                 = useState(0);
  const [title, setTitle]               = useState('');
  const [budget, setBudget]             = useState('');
  const [duration, setDuration]         = useState('30');
  const [targetAudience, setAudience]   = useState('');
  const [goal, setGoal]                 = useState('');
  const [generating, setGenerating]     = useState(false);
  const [plan, setPlan]                 = useState(null);
  const [error, setError]               = useState('');
  const [activeCampaignId, setActiveCampaignId] = useState(null);

  const canNext = [
    title.trim().length > 0,
    budget > 0 && duration > 0,
    targetAudience.trim().length > 5,
    goal.length > 0,
  ];

  const generatePlan = async () => {
    setGenerating(true);
    setError('');

    const GOAL_LABELS = {
      brand_awareness: 'Markenbekanntheit steigern',
      lead_gen:        'Leads generieren',
      sales:           'Verkäufe steigern',
      engagement:      'Community-Engagement erhöhen',
    };

    const prompt = `Du bist Marketing-Stratege. Antworte NUR mit validem JSON, kein Text davor oder danach.

Kampagne: "${title}" | Budget: ${budget}€ | ${duration} Tage | Zielgruppe: ${targetAudience} | Ziel: ${GOAL_LABELS[goal] || goal}

{"summary":"2 Sätze Strategie","poster":{"headline":"","subline":"","callToAction":"","visualConcept":"","rationale":""},"flyer":{"headline":"","bodyText":"2 Sätze","bulletPoints":["","",""],"callToAction":"","rationale":""},"videoScript":{"duration":"30","hook":"","mainMessage":"","scenes":[{"scene":1,"description":"","duration":"10 Sek."},{"scene":2,"description":"","duration":"10 Sek."}],"closingCTA":"","rationale":""},"socialMedia":[{"platform":"Instagram","contentType":"Reel","caption":"","postingTime":"","frequency":"","rationale":""},{"platform":"TikTok","contentType":"Video","caption":"","postingTime":"","frequency":"","rationale":""},{"platform":"Meta (Facebook)","contentType":"Post","caption":"","postingTime":"","frequency":"","rationale":""}],"budgetAllocation":{"print":{"percentage":0,"amount":0,"rationale":""},"socialAds":{"percentage":0,"amount":0,"rationale":""},"production":{"percentage":0,"amount":0,"rationale":""},"reserve":{"percentage":0,"amount":0,"rationale":""}},"timeline":[{"week":1,"phase":"","activities":["",""]},{"week":2,"phase":"","activities":["",""]}]}

Fülle alle Felder auf Deutsch aus. Prozente = 100. Beträge summieren zu ${budget}€.`;

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ANTHROPIC_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 4000,
          messages: [{ role: 'user', content: prompt }],
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || 'Claude API Fehler');

      const rawText = data.content?.[0]?.text || '';
      const jsonMatch = rawText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('Ungültige Antwort von KI');

      let plan;
      const raw = jsonMatch[0];
      const attempts = [
        () => JSON.parse(raw),
        () => JSON.parse(raw.replace(/,\s*([}\]])/g, '$1')),
        () => JSON.parse(raw.replace(/,\s*([}\]])/g, '$1').replace(/\n/g, ' ').replace(/\r/g, '')),
        () => JSON.parse(raw.replace(/[ -]/g, ' ').replace(/,\s*([}\]])/g, '$1')),
      ];
      let parsed = false;
      for (const attempt of attempts) {
        try { plan = attempt(); parsed = true; break; } catch { continue; }
      }
      if (!parsed) throw new Error('KI hat kein gültiges JSON zurückgegeben – bitte nochmal versuchen');

      setPlan(plan);
      setStep(5);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSavePlan = async (generatedPlan) => {
    const id = await saveCampaign({
      id: activeCampaignId || undefined,
      ownerId: user.uid,
      title,
      budget: Number(budget),
      duration: Number(duration),
      targetAudience,
      goal,
      status: 'draft',
      plan: generatedPlan,
      createdAt: activeCampaignId ? undefined : new Date().toISOString(),
    });
    setActiveCampaignId(id);
  };

  const handleReset = () => {
    setStep(0); setTitle(''); setBudget(''); setDuration('30');
    setAudience(''); setGoal(''); setPlan(null); setError('');
    setActiveCampaignId(null);
  };

  if (step === 5 && plan) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-8">
        <button onClick={onClose} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-6">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 6H2M5 3L2 6L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Zurück zu Agentur-Tools
        </button>
        <PlanResult plan={plan} campaign={{ title, budget, duration, targetAudience, goal }} onSave={handleSavePlan} onReset={handleReset} />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-8">
      <button onClick={onClose} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-6">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 6H2M5 3L2 6L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Zurück zu Agentur-Tools
      </button>

      <h2 className="text-xl font-semibold text-gray-900 mb-1">Kampagnen-Assistent</h2>
      <p className="text-sm text-gray-400 mb-6">Erstelle einen vollständigen Kampagnenplan in wenigen Schritten.</p>

      <StepIndicator current={step} />

      {/* Step 0: Titel */}
      {step === 0 && (
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2">Kampagnentitel</label>
          <p className="text-xs text-gray-400 mb-4">Gib deiner Kampagne einen aussagekräftigen Namen.</p>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && canNext[0] && setStep(1)}
            placeholder="z. B. Frühjahrs-Kollektion 2025"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition-all"
          />
        </div>
      )}

      {/* Step 1: Budget & Laufzeit */}
      {step === 1 && (
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2">Budget & Laufzeit</label>
          <p className="text-xs text-gray-400 mb-4">Wie viel steht zur Verfügung und wie lange soll die Kampagne laufen?</p>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Gesamtbudget (€)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">€</span>
                <input
                  autoFocus
                  type="number"
                  min="100"
                  value={budget}
                  onChange={e => setBudget(e.target.value)}
                  placeholder="5000"
                  className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Laufzeit (Tage)</label>
              <div className="flex gap-2 flex-wrap">
                {[7, 14, 30, 60, 90].map(d => (
                  <button
                    key={d}
                    onClick={() => setDuration(String(d))}
                    className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${duration === String(d) ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:border-indigo-300'}`}
                  >{d} Tage</button>
                ))}
                <input
                  type="number"
                  min="1"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  placeholder="Andere"
                  className="w-24 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-indigo-400"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Zielgruppe */}
      {step === 2 && (
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2">Zielgruppe</label>
          <p className="text-xs text-gray-400 mb-4">Beschreibe deine Zielgruppe so genau wie möglich.</p>
          <textarea
            autoFocus
            value={targetAudience}
            onChange={e => setAudience(e.target.value)}
            placeholder="z. B. Frauen und Männer, 25–40 Jahre, urban, design- und nachhaltigkeitsbewusst, mittleres bis hohes Einkommen"
            rows={4}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 resize-none"
          />
          <p className="text-xs text-gray-400 mt-2">Je mehr Details, desto präziser wird der Plan.</p>
        </div>
      )}

      {/* Step 3: Kampagnenziel */}
      {step === 3 && (
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2">Hauptziel</label>
          <p className="text-xs text-gray-400 mb-4">Was soll diese Kampagne primär erreichen?</p>
          <div className="grid grid-cols-2 gap-3">
            {GOALS.map(g => (
              <button
                key={g.value}
                onClick={() => setGoal(g.value)}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${goal === g.value ? 'border-indigo-600 bg-indigo-50 text-indigo-600' : 'border-gray-100 hover:border-indigo-200 bg-white text-gray-400'}`}
              >
                <span className="block mb-2">{GoalIcons[g.value]}</span>
                <p className="text-sm font-semibold text-gray-900">{g.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{g.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 4: Generierung */}
      {step === 4 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Zusammenfassung</h3>
          <div className="bg-gray-50 rounded-2xl p-5 mb-6">
            <table className="w-full text-sm border-separate" style={{borderSpacing: '0 8px'}}>
              <tbody>
                {[
                  ['Titel',      title],
                  ['Budget',     `${Number(budget).toLocaleString('de-DE')} €`],
                  ['Laufzeit',   `${duration} Tage`],
                  ['Zielgruppe', targetAudience],
                  ['Ziel',       GOALS.find(g => g.value === goal)?.label],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <td className="text-gray-500 pr-4 whitespace-nowrap align-top w-28">{label}</td>
                    <td className="font-medium text-gray-900 text-right align-top">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">{error}</div>
          )}

          <button
            onClick={generatePlan}
            disabled={generating}
            className="w-full py-3 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {generating ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                KI erstellt deinen Plan…
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Kampagnenplan generieren
              </>
            )}
          </button>
        </div>
      )}

      {/* Navigation */}
      {step < 4 && (
        <div className="flex justify-between mt-8">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            className="text-sm text-gray-400 hover:text-gray-600 disabled:opacity-0 transition-colors flex items-center gap-1"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 6H2M5 3L2 6L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Zurück
          </button>
          <button
            onClick={() => setStep(s => s + 1)}
            disabled={!canNext[step]}
            className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 px-5 py-2 rounded-xl transition-all flex items-center gap-1"
          >
            Weiter
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6H10M7 3L10 6L7 9" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}
